// Copyright 2026, University of Maryland, All Rights Reserved
/** Configure the named document slots exposed by SRM upload workflows. */
import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import { Button, Input, Label, Switch } from '@/components/ui'

import type { UploadSlot } from '../types'

interface AgentSlotEditorProps {
  slots: UploadSlot[]
  allowAdditionalSlots: boolean
  disabled?: boolean
  onSlotsChange: (slots: UploadSlot[]) => void
  onAllowAdditionalSlotsChange: (allowed: boolean) => void
}

interface SlotRowProps {
  slot: UploadSlot
  disabled: boolean
  onRename: (label: string) => void
  onRemove: () => void
}

function SlotRow({ slot, disabled, onRename, onRemove }: SlotRowProps) {
  const [label, setLabel] = useState(slot.label)
  useEffect(() => setLabel(slot.label), [slot.label])

  const saveLabel = () => {
    const next = label.trim()
    if (!next) {
      setLabel(slot.label)
    } else if (next !== slot.label) {
      onRename(next)
    }
  }

  return (
    <li className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
      <span className="font-mono text-muted-foreground">{slot.id}</span>
      <Input
        aria-label={`Label for slot ${slot.id}`}
        value={label}
        onChange={event => setLabel(event.target.value)}
        onBlur={saveLabel}
        disabled={disabled}
        className="h-8 flex-1"
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-destructive hover:text-destructive"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove slot ${slot.id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  )
}

export function AgentSlotEditor({
  slots,
  allowAdditionalSlots,
  disabled = false,
  onSlotsChange,
  onAllowAdditionalSlotsChange,
}: AgentSlotEditorProps) {
  const [newId, setNewId] = useState('')
  const [newLabel, setNewLabel] = useState('')

  const addSlot = () => {
    const id = newId.trim()
    const label = newLabel.trim()
    if (!id || !label || slots.some(slot => slot.id === id)) return
    onSlotsChange([...slots, { id, label }])
    setNewId('')
    setNewLabel('')
  }

  return (
    <section className="max-w-2xl space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-medium">Upload slots</h2>
        <p className="text-sm text-muted-foreground">
          Define the document roles shown in SRM upload requests. Slot IDs are routing keys and cannot be renamed.
        </p>
      </div>

      {slots.length > 0 ? (
        <ul className="space-y-2">
          {slots.map(slot => (
            <SlotRow
              key={slot.id}
              slot={slot}
              disabled={disabled}
              onRename={label => onSlotsChange(slots.map(item => item.id === slot.id ? { ...item, label } : item))}
              onRemove={() => onSlotsChange(slots.filter(item => item.id !== slot.id))}
            />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No slots defined.</p>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1">
          <Label htmlFor="agent-new-slot-id">Slot ID</Label>
          <Input
            id="agent-new-slot-id"
            value={newId}
            onChange={event => setNewId(event.target.value)}
            placeholder="e.g. dpq"
            disabled={disabled}
          />
        </div>
        <div className="flex-1 space-y-1">
          <Label htmlFor="agent-new-slot-label">Label</Label>
          <Input
            id="agent-new-slot-label"
            value={newLabel}
            onChange={event => setNewLabel(event.target.value)}
            placeholder="e.g. Data Privacy Questionnaire"
            disabled={disabled}
          />
        </div>
        <Button
          onClick={addSlot}
          disabled={disabled || !newId.trim() || !newLabel.trim() || slots.some(slot => slot.id === newId.trim())}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add slot
        </Button>
      </div>

      <div className="flex items-center gap-3 border-t pt-2">
        <Switch
          id="agent-allow-additional"
          checked={allowAdditionalSlots}
          onCheckedChange={onAllowAdditionalSlotsChange}
          disabled={disabled}
        />
        <Label htmlFor="agent-allow-additional" className="cursor-pointer">
          Allow additional (ad-hoc) slots beyond the defined list
        </Label>
      </div>
    </section>
  )
}
